<template>
  <div ref="rootEl" class="w-full relative h-[20px]">
    <svg class="absolute inset-0 size-full overflow-visible" fill="none">
      <path :d="path" stroke-width="1" :class="tint.stroke" />
    </svg>
  </div>
</template>

<script setup lang="ts">
import { ScoreLevel } from '@/shared/graphql/router/graphql'
import { computed, onMounted, onUnmounted, ref } from 'vue'

const props = withDefaults(
  defineProps<{
    markerPct: number
    legX?: number
    level?: ScoreLevel | null
  }>(),
  { legX: 0, level: null }
)

const BAR_Y = 13
const BOTTOM_Y = 20
const RADIUS = 6

const tint = computed(() => {
  if (props.level === ScoreLevel.High) {
    return { stroke: 'stroke-red-300' }
  }
  if (props.level === ScoreLevel.Medium) {
    return { stroke: 'stroke-yellow-400' }
  }
  if (props.level === ScoreLevel.Low) {
    return { stroke: 'stroke-green-400' }
  }
  return { stroke: 'stroke-gray-300' }
})

const rootEl = ref<HTMLElement>()
const width = ref(0)
let resizeObserver: ResizeObserver | null = null

const path = computed(() => {
  const w = width.value
  if (!w) return ''
  const mx = Math.min(Math.max((props.markerPct / 100) * w, 0.5), w - 0.5)
  const arm = (x: number) => {
    const dir = Math.sign(mx - x) || 1
    const r = Math.min(RADIUS, Math.abs(mx - x) / 2)
    return [
      `M ${x} ${BOTTOM_Y}`,
      `L ${x} ${BAR_Y + r}`,
      `Q ${x} ${BAR_Y} ${x + dir * r} ${BAR_Y}`,
      `L ${mx - dir * r} ${BAR_Y}`,
      `Q ${mx} ${BAR_Y} ${mx} ${BAR_Y - r}`,
      `L ${mx} 0`,
    ].join(' ')
  }
  return `${arm(props.legX)} ${arm(w)}`
})

onMounted(() => {
  if (!rootEl.value) return
  resizeObserver = new ResizeObserver(() => {
    width.value = rootEl.value?.clientWidth ?? 0
  })
  resizeObserver.observe(rootEl.value)
})

onUnmounted(() => {
  resizeObserver?.disconnect()
  resizeObserver = null
})
</script>
